import { Button, Space, TableProps, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { SensitiveWordItem } from '../types';
import ScrollTable from 'components/ScrollTable';
import { useTranslate } from '@tolgee/react';
import StatusButton from 'components/StatusButton';
import dayjs from 'dayjs';

type RT = SensitiveWordItem;

interface SensitiveWordTableProps extends TableProps<RT> {
  onEdit: (item: RT) => void;
  onDelete: (item: RT) => Promise<void>;
}

export default function SensitiveWordTable({
  onEdit,
  onDelete,
  ...otherProps
}: SensitiveWordTableProps) {
  const { t } = useTranslate();

  const columns: ColumnsType<RT> = [
    {
      title: t('敏感词内容'),
      dataIndex: 'word',
      key: 'word',
      width: 250,
      fixed: 'left',
    },
    {
      title: t('适用范围'),
      dataIndex: 'scopeType',
      key: 'scopeType',
      width: 250,
      render: (_, record) => {
        const { scopeType, platforms, regions, platformRegions } = record;

        switch (scopeType) {
          case 'ALL_PLATFORMS':
            return <Tag>{t('全部平台')}</Tag>;
          case 'SPECIFIED_PLATFORMS':
            return platforms?.map((platform) => (
              <Tag key={platform}>{platformLabelMap[toLowerCase(platform)] ?? platform}</Tag>
            ));
          case 'SPECIFIED_REGIONS':
            return regions?.map((region) => (
              <Tag key={region}>{t(`region.${toLowerCase(region)}`)}</Tag>
            ));
          case 'SPECIFIED_PLATFORMS_REGIONS':
            return Object.entries(platformRegions || {}).map(([platform, platformRegions]) => {
              const platformLabel =
                platformLabelMap[toLowerCase(platform as General.PlatformUpperCase)] ?? platform;
              return platformRegions.map((region) => (
                <Tag>{`${platformLabel} - ${t(`region.${toLowerCase(region)}`)}`}</Tag>
              ));
            });
          default:
            return '-';
        }
      },
    },
    {
      title: t('ViewBlockList.表格列操作时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 180,
      render: (value) => dayjs(Number(value)).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('ViewBlockList.表格列操作人'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 160,
      render: (value) => value ?? '-',
    },
    {
      title: t('操作'),
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button className="!pl-0" type="link" onClick={() => onEdit(record)}>
            {t('编辑')}
          </Button>
          <StatusButton type="link" danger onClick={() => onDelete(record)}>
            {t('phrase.删除')}
          </StatusButton>
        </Space>
      ),
    },
  ];

  return <ScrollTable rowKey="id" columns={columns} {...otherProps} />;
}
